import { redirect } from 'react-router';
import { type SupabaseClient } from '@supabase/supabase-js';
import type { QuizMetadata } from '@/types/quiz';
import { supabase } from '@/lib/supabase';
import type { QuizBySlugRow, QuizQuestion } from '@/lib/services/questions';
import {
  parseResolvedQuizSponsorsStylePreset,
  type ResolvedQuizSponsorsStylePreset,
} from '@/lib/domain/sponsorsStylePreset';
import { routes } from '@/routes';
import type { QuizRouteData } from '@/types/routes';

export type QuizPlaybackBootstrap = {
  quiz: QuizBySlugRow;
  questions: QuizQuestion[];
  sponsorsStyle: ResolvedQuizSponsorsStylePreset;
};

/**
 * Fetches the immutable public frame used to start a Quiz. The RPC resolves
 * routing, the current Live Edition's public items, and the public Sponsors &
 * Style revision in one database snapshot. Identity-scoped progress is loaded
 * separately by the Game Run query once its writable identity is ready.
 */
export async function fetchQuizPlaybackBootstrap(
  slug: string,
  client?: SupabaseClient
): Promise<QuizPlaybackBootstrap | null> {
  const { data, error } = await (client ?? supabase).rpc('get_quiz_playback_bootstrap', {
    p_slug: slug,
  });

  if (error) throw error;
  if (!data) return null;

  const payload = data as {
    quiz?: QuizBySlugRow;
    questions?: QuizQuestion[];
    sponsorsStyle?: unknown;
  };
  if (!payload.quiz || !Array.isArray(payload.questions) || payload.sponsorsStyle === undefined) {
    throw new Error('Quiz Playback bootstrap returned an invalid payload');
  }

  return {
    quiz: payload.quiz,
    questions: payload.questions,
    sponsorsStyle: parseResolvedQuizSponsorsStylePreset(payload.sponsorsStyle),
  };
}

export function getCanonicalQuizSlugRedirect(
  requestedSlug: string,
  quiz: Pick<QuizMetadata, 'is_alias' | 'canonical_slug'>,
  search = ''
): globalThis.Response | null {
  const canonicalSlug = quiz.canonical_slug?.trim().toLowerCase();
  if (
    quiz.is_alias !== true ||
    !canonicalSlug ||
    canonicalSlug === requestedSlug.trim().toLowerCase()
  ) {
    return null;
  }

  return redirect(`${routes.quiz(canonicalSlug)}${search}`);
}

export async function fetchQuizRouteData(
  slug: string,
  client: SupabaseClient
): Promise<QuizRouteData> {
  try {
    const bootstrap = await fetchQuizPlaybackBootstrap(slug, client);
    if (!bootstrap) {
      return { status: 'not_found' as const, quiz: null, questions: [], sponsorsStyle: null };
    }

    const quiz = bootstrap.quiz as QuizMetadata;
    if (quiz.lifecycle !== 'live') {
      return { status: 'not_live' as const, quiz, questions: [], sponsorsStyle: null };
    }

    if (!bootstrap.questions.length) {
      return { status: 'load_error' as const, quiz, questions: [], sponsorsStyle: null };
    }

    return {
      status: 'ok' as const,
      quiz,
      questions: bootstrap.questions,
      sponsorsStyle: bootstrap.sponsorsStyle,
    };
  } catch (error) {
    console.error('Error fetching Quiz Playback bootstrap:', error);
    throw error;
  }
}

export async function loadQuizRoute(
  slug: string,
  client: SupabaseClient,
  url: URL
): Promise<QuizRouteData | Response> {
  const data = await fetchQuizRouteData(slug, client);
  const aliasRedirect = data.quiz
    ? getCanonicalQuizSlugRedirect(slug, data.quiz, url.search)
    : null;

  return aliasRedirect ?? data;
}
